// 2.2 / 3.2 / 3.3 — the persistent form anchor at the top of chat.
//
// Anatomy per the design reference: icon tile, short name, live progress bar
// with tabular percent, the AI-review caption when suggestions are pending, an
// explicit OPEN action, and a separate discard rail. The chat body supplies
// `onOpen` (the form-view overlay).
//
// The handler-less guard stays: without `onOpen` there is nothing to open, and
// a focusable button announced as "Open …" that does nothing when pressed is an
// affordance that lies — worse than an absent one. With no handler the card
// degrades to a plain status region — icon, name, progress — and the discard
// rail stays interactive.

import { useEffect, useLayoutEffect, useRef } from 'react';
import { DISCARD_CONFIRM_QUESTION, EDITS_NOT_SENT_LABEL, FLIP_EASING, FLIP_MS } from './constants';
import styles from './ConversationalForms.module.css';
import { useConversationalForms } from './FormsContext';
import { AlertIcon, ChevronDownIcon, ClipboardIcon, CloseIcon, SparklesIcon } from './icons';
import { useEscLayer, useFocusTrap } from './useFocusTrap';
import { useReducedMotion } from './useReducedMotion';

type PinnedFormCardProps = {
    onOpen?: () => void;
    /** Multiform: the forms-menu toggle. Handler-less guard as with `onOpen` —
     *  without it (single-form tenants) no menu control renders at all. */
    onToggleMenu?: () => void;
    menuOpen?: boolean;
};

/** Copy per write state. `error` is honest local retention, not a dead end —
 *  the values are kept on the device and the next write re-sends them. */
const SAVE_CUE_TEXT = {
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Saved on this device',
} as const;

const PinnedFormCard = ({ onOpen, onToggleMenu, menuOpen }: PinnedFormCardProps) => {
    const {
        active,
        form,
        registry,
        state,
        pct,
        pendingCount,
        requestDiscard,
        keepForm,
        discardForm,
        confirmAllSuggestions,
        openReview,
        flipRectRef,
    } = useConversationalForms();
    const cardRef = useRef<HTMLDivElement>(null);
    const railRef = useRef<HTMLButtonElement>(null);
    const reducedMotion = useReducedMotion();

    // The discard confirm is a layer like any other (task 5.1): Esc peels it
    // alone — answering "Keep" — and the trap holds Tab within Keep/Discard,
    // handing focus back to the discard rail when the confirm goes away.
    useEscLayer(keepForm, state.confirmingDiscard);

    // The trap below restores focus to whatever `document.activeElement` was
    // when ITS OWN effect ran — not to whichever surface asked for the
    // discard. Asked from this rail, that's already the rail. Asked from
    // inside the overlay (its own Discard button), the overlay closes in the
    // very same commit: its trap unmounts and, in the passive-effect
    // "destroy" pass, hands focus back to this card's Open control — before
    // any new effect gets to run. Left alone, the confirm trap's "create"
    // pass (which always runs after every "destroy") would then capture
    // Open instead of the rail (store tracker 6.37a). Refocusing the rail
    // here — a plain effect declared before the trap, so its create() fires
    // first within that same create pass — makes the trap's own capture
    // land on the rail either way, with one restore path instead of two.
    useEffect(() => {
        if (state.confirmingDiscard) railRef.current?.focus({ preventScroll: true });
    }, [state.confirmingDiscard]);
    const confirmTrapRef = useFocusTrap<HTMLDivElement>(state.confirmingDiscard);

    // FLIP: glide from the start card's captured rect to the pinned slot, so the
    // card reads as the same object moving rather than one appearing.
    useLayoutEffect(() => {
        const from = flipRectRef.current;
        const el = cardRef.current;
        if (!from || !el) return;
        flipRectRef.current = null;
        if (reducedMotion) return; // instant swap, no glide

        const to = el.getBoundingClientRect();
        if (!to.width || !to.height) return;
        const dx = from.left - to.left;
        const dy = from.top - to.top;
        const sx = from.width / to.width;
        const sy = from.height / to.height;

        el.style.transformOrigin = 'top left';
        el.style.transition = 'none';
        el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
        el.getBoundingClientRect(); // force reflow before transitioning

        const raf = requestAnimationFrame(() => {
            el.style.transition = `transform ${FLIP_MS}ms ${FLIP_EASING}`;
            el.style.transform = 'none';
        });
        const cleanup = setTimeout(() => {
            el.style.transition = '';
            el.style.transform = '';
            el.style.transformOrigin = '';
        }, FLIP_MS + 80);

        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(cleanup);
        };
    }, [state.activeFormId, flipRectRef, reducedMotion]);

    // The identity check matters under multiform: the provider presents the
    // *active* form here, and a stale id (a form unpublished mid-session)
    // presents nothing rather than the wrong form's card.
    const showActive = active && !!form && !!state.activeFormId && form.objectId === state.activeFormId;

    // Forms-corrections slice (D2): a submitted form with an unsent edit gets
    // a persistent, visible home here — independent of `activeFormId`, since
    // reviewing never pins the form. Resolved through the registry so a
    // dirty form other than whatever is currently active (if anything is)
    // still shows its own row.
    const dirtyForms = active
        ? state.dirtyFormIds
              .map((id) => registry?.byId.get(id))
              .filter((f): f is NonNullable<typeof f> => f !== undefined)
        : [];

    // Review-fix (save-cue visibility): a confirm/submit write's target is
    // the form that was active when the write started — but the student can
    // switch to a *different* form (pausing the first, multiform) before
    // that write settles. The inline cue below is gated on matching the
    // pinned form exactly, so a write for any other form used to surface
    // nowhere while a new form was pinned in its place — a failure was as
    // silent as a success. `cueForm` names whichever form the cue actually
    // belongs to. This is deliberately scoped to "another form is pinned
    // instead" (`showActive`) — the ordinary quiescent state right after a
    // submit, with the just-submitted form's cue still set and nothing
    // pinned at all, is not this bug and must keep rendering nothing, same
    // as before.
    //
    // Narrowed to the FAILURE state only (adversarial-review fix): the
    // reported gap is specifically that a persist FAILURE surfaces nowhere.
    // `saving`/`saved` are transient and nothing ever returns `saveCue` to
    // `idle` on its own — showing this row for those too would leave
    // "Your Application: Saved" parked on another form's card indefinitely,
    // long after the write that earned it. A failure earns a standing
    // surface (it needs a retry); a success does not.
    const cueText = state.saveCue === 'error' ? SAVE_CUE_TEXT.error : null;
    const cueForm =
        cueText !== null && showActive && state.saveCueFormId && state.saveCueFormId !== form?.objectId
            ? registry?.byId.get(state.saveCueFormId) ?? null
            : null;
    const cueNeedsOwnRow = cueForm !== null;

    if (!showActive && !dirtyForms.length) return null;

    const reviewCaption =
        pendingCount > 0
            ? `${pendingCount} AI ${pendingCount === 1 ? 'answer' : 'answers'} to review`
            : null;

    const body = showActive && (
        <>
            <span className={styles.pinnedIcon}>
                <ClipboardIcon />
            </span>
            <span className={styles.pinnedText}>
                <span className={styles.pinnedTitle}>{form!.short}</span>
                <span className={styles.pinnedProgress}>
                    <span
                        className={styles.track}
                        role="progressbar"
                        aria-label="Form completion"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <i className={styles.trackFill} style={{ width: `${pct}%` }} />
                    </span>
                    <span className={styles.pct}>{pct}%</span>
                </span>
                {reviewCaption && (
                    <span className={styles.reviewCaption}>
                        <SparklesIcon /> {reviewCaption}
                    </span>
                )}
            </span>
        </>
    );

    return (
        <div className={styles.pinnedWrap} data-testid="cf-pinned-card">
            {showActive && (
                <>
                    {/* The element the FLIP float animates — named so the motion
                        behaviour is assertable without reaching into the DOM tree. */}
                    <div className={styles.pinned} ref={cardRef} data-testid="cf-pinned-animated">
                        {onOpen ? (
                            <button
                                type="button"
                                className={styles.pinnedMain}
                                onClick={onOpen}
                                aria-label={`Open ${form!.short} — ${pct}% complete`}
                            >
                                {body}
                                <span className={styles.openLabel}>Open</span>
                            </button>
                        ) : (
                            <div className={styles.pinnedMain}>{body}</div>
                        )}
                        {onToggleMenu && (
                            <button
                                type="button"
                                className={styles.menuRail}
                                onClick={onToggleMenu}
                                aria-label="Your forms"
                                aria-expanded={menuOpen === true}
                                data-testid="cf-menu-toggle"
                            >
                                <ChevronDownIcon />
                            </button>
                        )}
                        <button
                            type="button"
                            ref={railRef}
                            className={styles.discardRail}
                            onClick={requestDiscard}
                            aria-label="Discard form"
                            aria-expanded={state.confirmingDiscard}
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {state.confirmingDiscard && (
                        <div
                            ref={confirmTrapRef}
                            className={styles.discardConfirm}
                            role="alertdialog"
                            aria-label="Discard form"
                        >
                            <span className={styles.discardQuestion}>{DISCARD_CONFIRM_QUESTION}</span>
                            <span className={styles.discardActions}>
                                <button type="button" className={styles.discardKeep} onClick={keepForm}>
                                    Keep
                                </button>
                                <button type="button" className={styles.discardGo} onClick={discardForm}>
                                    Discard
                                </button>
                            </span>
                        </div>
                    )}

                    {/* Save cue — tracks the real POST lifecycle and renders only once a
                        write has actually happened; a plain local edit shows nothing
                        (harvest 0.3). Announced politely, never focused.
                        Review-fix: a correction re-post may target a different
                        submitted form entirely (review is decoupled from
                        `activeFormId`), so this card only claims the cue when
                        it is actually the write's target — otherwise it would
                        misattribute another form's "Saved" to itself. */}
                    {state.saveCue !== 'idle' && state.saveCueFormId === form!.objectId && (
                        <span className={styles.saveCue} role="status" data-testid="cf-save-cue">
                            {SAVE_CUE_TEXT[state.saveCue]}
                        </span>
                    )}

                    {/* Confirm-all affordance — only while something is pending. */}
                    {pendingCount > 0 && (
                        <div className={styles.suggestionBar}>
                            <span className={styles.suggestionLabel}>
                                {pendingCount} filled by assistant — review
                            </span>
                            <button
                                type="button"
                                className={styles.suggestionConfirmAll}
                                onClick={confirmAllSuggestions}
                            >
                                Confirm all
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Forms-corrections slice (D2): one warning row per submitted
                form with an unsent edit — its own persistent, visible home,
                independent of whatever (if anything) is actively pinned. */}
            {dirtyForms.map((dirtyForm) => (
                <div
                    key={dirtyForm.objectId}
                    className={styles.dirtyWarnRow}
                    data-testid="cf-dirty-warning"
                >
                    <AlertIcon size={13} />
                    <span className={styles.dirtyWarnLabel}>
                        <strong>{dirtyForm.short}</strong>: {EDITS_NOT_SENT_LABEL}
                    </span>
                    <button
                        type="button"
                        className={styles.dirtyWarnAction}
                        onClick={() => openReview(dirtyForm.objectId)}
                        aria-label={`Review ${dirtyForm.short}`}
                    >
                        Review
                    </button>
                </div>
            ))}

            {/* Save cue, re-targeted (review-fix): the write this describes
                belongs to `cueForm`, not (or no longer) whatever is pinned —
                its own small row, named, rather than the silent surface a
                mismatch used to leave. Mitigations already make the inline
                cue informational-only and self-healing on the next write;
                this is the same honesty extended to the one case it didn't
                cover, not a new notification system. */}
            {cueNeedsOwnRow && (
                <span className={styles.saveCue} role="status" data-testid="cf-save-cue-row">
                    <strong>{cueForm!.short}</strong>: {cueText}
                </span>
            )}
        </div>
    );
};

export default PinnedFormCard;
